/**
 * Copyright (c) 2022 Nueva AG
 *
 * A composable managing cookies and localstorage
 *
 * @summary Cookie and Local Storage handling helpers
 * @author Luca Margadant <luca.margadant@nueva.ch>
 */

/*
    Sets a cookie
    @param name (string) Mandatory - the desired name of the cookie
    @param value (string) Mandatory - the desired value of the cookie
    @param days (string) Optinal - The days to life for a cookie
*/
export const setCookie = (name, value, days) => {
    if (!name || !value) {
        throw createError({
            statusCode: '500',
            statusMessage: 'Fatal: No name or value given for cookie @setCookie @useHelpers',
        });
    }

    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = `; expires=${date.toUTCString()}`;
    }

    document.cookie = `${name}=${value || ''}${expires}; path=/`;
};

/*
    Get a cookie by name
    @param name (string) Mandatory - the name of the cookie
*/
export const getCookie = (name, all = false) => {
    if (!name) {
        throw createError({
            statusCode: '500',
            statusMessage: 'Fatal: No name for cookie to get @getCookie @useHelpers',
        });
    }
    const cookie = {};
    document.cookie.split(';').forEach((el) => {
        const [k, v] = el.split('=');
        cookie[k.trim()] = v;
    });

    if (all) return cookie;
    return cookie[name];
};

/*
    Delete a cookie by name
    @param name (string) Mandatory - the name of the cookie
*/
export const deleteCookie = (name) => {
    if (!name) {
        throw createError({
            statusCode: '500',
            statusMessage: 'Fatal: No name for cookie to delete @deleteCookie @useHelpers',
        });
    }
    document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

/*
    Set a value to localstorage with desired name
    @param name (string) Mandatory - the desired name of the item
    @param value (string) Mandatory - the desired value of the item
*/
export const setLocalStorageItem = (name, value) => {
    if (!name || !value) {
        throw createError({
            statusCode: '500',
            statusMessage: 'Fatal: No name or value given for local storage item @setLocalStorageItem @useHelpers',
        });
    }
    localStorage.setItem(name, value);
};

/*
    Get a localstorage item by name
    @param name (string) Mandatory - the name of the item
*/
export const getLocalStorageItem = (name) => {
    if (!name) {
        throw createError({
            statusCode: '500',
            statusMessage: 'Fatal: No name for local storage item to get @getLocalStorageItem @useHelpers',
        });
    }
    return localStorage.getItem(name);
};

/*
    Delete a localstorage item by name
    @param name (string) Mandatory - the name of the item
*/
export const deleteLocalStorageItem = (name) => {
    if (!name) {
        throw createError({
            statusCode: '500',
            statusMessage: 'Fatal: No name for local storage item to delete @deleteLocalStorageItem @useHelpers',
        });
    }
    localStorage.removeItem(name);
};

/*
    Clear the complete local storage
*/
export const clearLocalStorage = () => {
    localStorage.clear();
};
